import React from 'react';
import PropTypes from 'prop-types';
import _, { get } from 'lodash';
import cn from 'classnames';
import EmptyState from './EmptyState';

const SKELETON = ['s1', 's2', 's3', 's4', 's5'];

function List({
  data,
  render,
  isLoading,
  selectedId,
  onSelectRow,
  itemClassName,
  linkClassName,
  containerClassName,
  selectedKey,
  emptyGlyph,
  emptyStatement,
  emptyGuidance,
  emptyAction,
}) {
  const checkDataType = (item) => {
    try {
      if (typeof render === 'string') return _.get(item, render);
      if (typeof render === 'function') return render(item);
      return '-';
    } catch (err) {
      return '- ERR -';
    }
  };

  const handleSelectItem = (e, item) => {
    e.preventDefault();
    onSelectRow(item);
  };

  return (
    <ul className={cn('w-64 flex-grow', containerClassName)}>
      {isLoading || (data || [])?.length < 1 ? (
        <div className={cn(isLoading ? 'h-auto' : 'h-full')}>
          {isLoading ? (
            <div className="p-3 space-y-2">
              {SKELETON.map((k) => (
                <div
                  key={k}
                  className="animate-pulse bg-neutral-100 h-14 w-full rounded"
                />
              ))}
            </div>
          ) : (
            // Rail owns the empty message and the create action (audit 6.7);
            // the detail pane never repeats it.
            <EmptyState
              glyph={emptyGlyph}
              statement={emptyStatement}
              guidance={emptyGuidance}
              action={emptyAction}
            />
          )}
        </div>
      ) : (
        (data || [])?.map((item) => (
          <li
            key={get(item, selectedKey)}
            onClick={(e) => handleSelectItem(e, item)}
            className={cn(
              'list-none cursor-pointer transition-colors border-b border-neutral-100',
              // Selection is one treatment product-wide: brand-50 fill plus an
              // inset 3px brand-600 bar. Replaces the flat mid-grey fill that
              // read as *disabled* — audit finding 6.1.
              selectedId === get(item, selectedKey)
                ? 'bg-brand-50 shadow-[inset_3px_0_0_rgb(var(--brand-600))]'
                : 'bg-white hover:bg-neutral-100',
              itemClassName,
            )}
            aria-hidden="true"
          >
            <a
              href="/"
              className={cn(
                'block hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600',
                selectedId === get(item, selectedKey)
                  ? 'text-brand-700 hover:text-brand-700'
                  : 'text-neutral-700',
                linkClassName,
              )}
            >
              {checkDataType(item)}
            </a>
          </li>
        ))
      )}
    </ul>
  );
}

List.defaultProps = {
  emptyGlyph: 'records',
  emptyStatement: 'There are no items.',
  emptyGuidance: '',
  emptyAction: null,
  containerClassName: '',
  itemClassName: '',
  linkClassName: '',
  selectedId: '',
  isLoading: false,
  render: 'id',
  selectedKey: 'uuid',
};

List.propTypes = {
  emptyGlyph: PropTypes.string,
  emptyStatement: PropTypes.string,
  emptyGuidance: PropTypes.string,
  emptyAction: PropTypes.node,
  data: PropTypes.instanceOf(Array).isRequired,
  itemClassName: PropTypes.string,
  linkClassName: PropTypes.string,
  onSelectRow: PropTypes.func.isRequired,
  containerClassName: PropTypes.string,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLoading: PropTypes.bool,
  render: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  selectedKey: PropTypes.string,
};

export default List;
